import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, MapPin, Globe, Clock, Ticket, ArrowLeft, ExternalLink, User, Download, Pencil } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loading, EmptyState } from "@/components/common/Loading";
import { RichTextView } from "@/components/forms/RichTextEditor";
import { BookmarkButton } from "@/components/social/ProfileActions";
import { ReportMenu } from "@/components/social/ReportMenu";
import { AskQuestionButton } from "@/components/social/AskQuestionDialog";
import { PublicQna } from "@/components/social/PublicQna";
import { EventComments } from "@/components/social/EventComments";
import { InterestedButton } from "@/components/social/InterestedButton";
import { AddToCalendar } from "@/components/events/AddToCalendar";
import { useReferences, labelFor } from "@/hooks/useReferences";
import { eventDateParts, viewerTimeHint, zoneAbbr } from "@/lib/timeago";
import { preferredTimezone, toIana } from "@/lib/timezones";
import { useAuth } from "@/context/AuthContext";
import LockedView from "@/pages/public/LockedView";
import { trackAction, trackOutboundLink, trackInternalLink, boundedLabel, hostOf } from "@/lib/analytics";

const fullAddress = (l) => {
  if (!l) return "";
  const stateZip = [l.state, l.zip].filter(Boolean).join(" ");
  const parts = [l.line1, l.line2, l.city, stateZip].map((s) => (s || "").trim()).filter(Boolean);
  // A lone country isn't a real address.
  if (parts.length === 0) return "";
  return [...parts, (l.country || "").trim()].filter(Boolean).join(", ");
};
// Render an embedded map only when we have a real street-level address: line1 + city + state.
const hasMappableAddress = (l) => {
  if (!l) return false;
  return Boolean((l.line1 || "").trim() && (l.city || "").trim() && (l.state || "").trim());
};
const mapsHref = (q) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
const mapsEmbedSrc = (q) => `https://www.google.com/maps?q=${encodeURIComponent(q)}&output=embed`;

export default function PublicEvent() {
  const { slug } = useParams();
  const { references } = useReferences();
  const { user, loading } = useAuth();
  const [ev, setEv] = useState(undefined);

  useEffect(() => {
    if (loading || !user || !user.email_verified) return;
    let ok = true;
    setEv(undefined);
    api.get(`/events/public/${slug}`).then(({ data }) => { if (ok) setEv(data); }).catch(() => { if (ok) setEv(null); });
    return () => { ok = false; };
  }, [user, loading, slug]);

  if (loading) return <div className="py-20"><Loading /></div>;
  if (!user || !user.email_verified) return <LockedView what="this event" />;
  if (ev === undefined) return <div className="py-20"><Loading /></div>;
  if (ev === null) return (
    <div className="mx-auto max-w-3xl px-4 py-20">
      <EmptyState icon={Calendar} title="Event not found" description="This event may have been removed or isn't published." action={<Link to="/events"><Button>Browse events</Button></Link>} />
    </div>
  );

  // Event times are stored as a wall clock in the event's own zone; `ev.timezone` is a
  // reference slug ("america-chicago") whose label is the IANA name Intl understands.
  const evTz = !ev.timezone ? "" : toIana(references.timezones, ev.timezone);
  const start = eventDateParts(ev.start_at);
  const end = eventDateParts(ev.end_at);
  // The viewer's own zone is whatever they chose in Account Settings (browser only as a fallback).
  const yourTime = viewerTimeHint(ev.start_at, evTz, preferredTimezone());
  const isVirtual = ev.format === "virtual" || ev.format === "hybrid";
  // Multi-day events must show BOTH dates — an end time alone reads as a same-day event.
  const isMultiDay = Boolean(ev.end_at && ev.end_at.slice(0, 10) !== (ev.start_at || "").slice(0, 10));

  return (
    <div className="bg-background">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="mt-4 flex items-center justify-between gap-3">
          <Link to="/events" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> All events</Link>
          {ev.is_owner && (
            <Link to={`/dashboard/events/${ev.id}`} data-testid="event-edit-button">
              <Button size="sm" variant="secondary" className="shadow-sm"><Pencil className="mr-1.5 h-4 w-4" /> Edit event</Button>
            </Link>
          )}
        </div>
        <div className="flex flex-col gap-6 py-6 lg:grid lg:grid-cols-3">
          {/* Main — `contents` on mobile so the side column can slot in ahead of comments */}
          <div className="contents lg:block lg:space-y-6 lg:col-span-2">
            <div className="aspect-[16/9] w-full overflow-hidden rounded-xl bg-gradient-to-r from-teal-100 to-teal-50 shadow-[0_8px_30px_-12px_rgba(16,24,40,0.25)]" data-testid="event-cover">
              {ev.cover_url && <img src={fileUrl(ev.cover_url)} alt="" className="h-full w-full object-cover" />}
            </div>
            <div>
              <div className="flex flex-wrap gap-1.5">
                {ev.category && <Badge className="bg-teal-50 text-teal-700">{labelFor(references.event_categories, ev.category)}</Badge>}
                {ev.format && <Badge variant="secondary">{labelFor(references.event_formats, ev.format)}</Badge>}
                {ev.source === "internal" && <Badge variant="secondary">Official</Badge>}
              </div>
              <h1 className="mt-3 text-3xl font-[720] tracking-[-0.02em] text-foreground" data-testid="event-detail-title">{ev.title}</h1>
              {ev.summary && <p className="mt-2 text-lg text-muted-foreground">{ev.summary}</p>}
            </div>
            {((ev.description && ev.description !== "<p></p>") || (ev.files || []).length > 0) && (
              <Card className="border-0 p-6">
                {ev.description && ev.description !== "<p></p>" && (
                  <div>
                    <h2 className="text-lg font-[640]">About this event</h2>
                    <div className="mt-3"><RichTextView html={ev.description} className="prose-so-spacious leading-[1.35]" /></div>
                  </div>
                )}
                {(ev.files || []).length > 0 && (
                  <div className={ev.description && ev.description !== "<p></p>" ? "mt-6" : ""}>
                    <h2 className="text-lg font-[640]">Downloads</h2>
                    <div className="mt-3 space-y-2">
                      {ev.files.map((f, i) => (
                        <a key={i} href={fileUrl(f.url)} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-lg border border-border bg-secondary/40 px-3 py-2 transition-colors hover:bg-secondary" data-testid={`event-file-${i}`}>
                          <Download className="h-4 w-4 text-primary" />
                          <span className="flex-1 truncate text-sm text-foreground">{f.name || "Attachment"}</span>
                          <ExternalLink className="h-4 w-4 text-muted-foreground" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            )}
            {((ev.industries || []).length > 0 || (ev.topics || []).length > 0) && (
              <Card className="border-0 p-6" data-testid="event-tags">
                {(ev.industries || []).length > 0 && (
                  <div data-testid="event-industries-tags">
                    <h2 className="text-lg font-[640]">Industries</h2>
                    <div className="mt-3 flex flex-wrap gap-1.5">{ev.industries.map((t) => <Badge key={t} className="bg-teal-50 text-teal-700">{labelFor(references.industries, t)}</Badge>)}</div>
                  </div>
                )}
                {(ev.topics || []).length > 0 && (
                  <div className={(ev.industries || []).length > 0 ? "mt-6" : ""} data-testid="event-topics-tags">
                    <h2 className="text-lg font-[640]">Topics</h2>
                    <div className="mt-3 flex flex-wrap gap-1.5">{ev.topics.map((t) => <Badge key={t} variant="secondary">{labelFor(references.resource_topics, t)}</Badge>)}</div>
                  </div>
                )}
              </Card>
            )}
            <div className="order-last lg:order-none"><EventComments eventId={ev.id} /></div>
          </div>

          {/* Side */}
          <div className="space-y-4">
            <Card className="relative border-0 p-6">
              <div className="absolute right-2 top-2 z-20">
                <ReportMenu contentType="event" contentId={ev.id} ownerUserId={ev.owner_id} isOwner={ev.is_owner} className="h-8 w-8 rounded-full border border-border/60 bg-card/80 shadow-sm backdrop-blur-sm hover:bg-card" testId="report-menu-event" />
              </div>
              {/* Primary CTA — moved to the top of the side card per spec. */}
              {ev.registration_url && (
                <a
                  href={ev.registration_url}
                  target="_blank"
                  rel="noreferrer"
                  className="block"
                  onClick={() => trackAction("event_cta_clicked", {
                    entityType: "event",
                    entityId: ev.id,
                    meta: {
                      cta_label: boundedLabel((ev.cta_label || "").trim() || "Register"),
                      destination_host: hostOf(ev.registration_url),
                    },
                  })}
                >
                  <Button className="w-full" data-testid="event-register">{(ev.cta_label || "").trim() || "Register"}</Button>
                </a>
              )}
              <div className={`space-y-4 ${ev.registration_url ? "mt-5" : ""}`}>
                <div className="flex items-start gap-3">
                  <Calendar className="mt-0.5 h-5 w-5 text-primary" />
                  <div>
                    <div className="font-[600] text-foreground">{start.dateLong}</div>
                    {isMultiDay && (
                      <div className="font-[600] text-foreground" data-testid="event-end-date">to {end.dateLong}</div>
                    )}
                    <div className="text-sm text-muted-foreground" data-testid="event-local-time">
                      {start.time}{end.time ? ` – ${end.time}` : ""}
                      {evTz ? ` ${zoneAbbr(ev.start_at, evTz)} (${evTz})` : ""}
                    </div>
                    {yourTime && (
                      <div className="text-xs text-muted-foreground/80" data-testid="event-viewer-time">{yourTime} your time</div>
                    )}
                  </div>
                </div>
                {/* Price moved directly under the date per spec. */}
                {ev.price && <div className="flex items-start gap-3"><Ticket className="mt-0.5 h-5 w-5 text-primary" /><div className="text-sm text-foreground">{ev.price}</div></div>}
                <div className="flex items-start gap-3">
                  {isVirtual ? <Globe className="mt-0.5 h-5 w-5 text-primary" /> : <MapPin className="mt-0.5 h-5 w-5 text-primary" />}
                  <div className="flex-1 text-sm text-foreground" data-testid="event-location">
                    {isVirtual && !hasMappableAddress(ev.location) ? "Online event" : (() => {
                      const venue = ev.location && ev.location.venue;
                      const addr = fullAddress(ev.location);
                      const mappable = hasMappableAddress(ev.location);
                      if (!venue && !addr) return "Location TBA";
                      const fullQ = [venue, addr].filter(Boolean).join(", ");
                      return (
                        <>
                          {venue && <div className="font-[600]">{venue}</div>}
                          {addr && (
                            <a href={mapsHref(fullQ)} target="_blank" rel="noreferrer" className="text-primary hover:underline" data-testid="event-location-map" onClick={() => trackOutboundLink("event", ev.id, "map", mapsHref(fullQ))}>{addr}</a>
                          )}
                          {ev.location_notes && (
                            <div className="mt-2 rounded-md bg-secondary/50 p-2.5 text-sm text-foreground" data-testid="event-location-notes">
                              <RichTextView html={ev.location_notes} />
                            </div>
                          )}
                          {mappable && (
                            <div className="mt-3 overflow-hidden rounded-lg border border-border" data-testid="event-location-map-embed">
                              <iframe
                                title={`Map of ${venue || addr}`}
                                src={mapsEmbedSrc(fullQ)}
                                width="100%"
                                height="200"
                                style={{ border: 0 }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                allowFullScreen
                              />
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
              <div className="mt-5 space-y-2">
                {isVirtual && ev.online_url && <a href={ev.online_url} target="_blank" rel="noreferrer" className="block"><Button variant="secondary" className="w-full"><ExternalLink className="mr-2 h-4 w-4" /> Join link</Button></a>}
                <InterestedButton eventId={ev.id} preview={ev.interested_preview} />
                <AddToCalendar calendar={ev.calendar} />
                <BookmarkButton itemType="event" itemId={ev.id} className="w-full justify-center" />
              </div>
            </Card>

            {ev.organizer_type === "external" ? (
              <Card className="border-0 p-6" data-testid="event-external-host-card">
                <h3 className="text-sm font-[640] uppercase tracking-wide text-muted-foreground">Hosted by</h3>
                <div className="mt-3 text-sm font-[600] text-foreground" data-testid="event-external-host-name">{ev.hosted_by || ev.host_name}</div>
              </Card>
            ) : (
            <Card className="border-0 p-6">
              <h3 className="text-sm font-[640] uppercase tracking-wide text-muted-foreground">Hosted by</h3>
              <div className="mt-3 flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  {(ev.organizer?.profile_image_url || ev.host?.headshot_url) && <AvatarImage src={fileUrl(ev.organizer?.profile_image_url || ev.host?.headshot_url)} alt="" />}
                  <AvatarFallback className="bg-teal-50 text-teal-700"><User className="h-4 w-4" /></AvatarFallback>
                </Avatar>
                <div className="text-sm">
                  <div className="font-[600] text-foreground">{ev.organizer?.display_name || ev.host_name || ev.host?.display_name || "Host"}</div>
                  {ev.organizer?.slug && (
                    <Link
                      to={`${ev.organizer.target_type === "business" ? "/b/" : "/p/"}${ev.organizer.slug}`}
                      className="text-primary hover:underline"
                      data-testid="event-organizer-link"
                      onClick={() => {
                        if (ev.organizer.target_type && ev.organizer.target_id) {
                          trackInternalLink("event", ev.id, "organizer",
                            ev.organizer.target_type, ev.organizer.target_id);
                        }
                      }}
                    >
                      View profile
                    </Link>
                  )}
                </div>
              </div>
            </Card>
            )}

            <PublicQna
              targetType="event"
              targetId={ev.id}
              ask={!ev.is_owner ? <AskQuestionButton targetType="event" targetId={ev.id} targetName={ev.title} /> : null}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
